/**
 * Subscription Context for managing subscription state.
 *
 * Provides subscription data and actions throughout the application.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import {
  SubscriptionPlan,
  CurrentSubscription,
  FeatureAccess,
  UsageInfo,
  PlanTier,
  PLAN_TIERS,
  ClientConfig,
  SurveyChatTarget,
} from '../../types/subscription';
import { subscriptionService } from '../../services/subscription/subscriptionService';
import { useAuth } from '../AuthContext';
import { entitlementsAreLoading, MARKETPLACE_ACCOUNT_TYPE } from './entitlementsResolution';

interface SubscriptionContextType {
  /** TRUE until this account's entitlements have been answered once.
   *
   *  Distinct from `loading`, which is only set INSIDE `refreshSubscription` and is therefore false before
   *  the first request — the state in which the dashboard drew every card, locked, with an upsell, because
   *  "not yet known" and "unrestricted and unpaid" were the same values. A consumer that does not read this
   *  behaves exactly as it did before. See entitlementsResolution.ts. */
  entitlementsLoading: boolean;
  /** Sidebar destinations this account may reach, or 'all'. Spec 25 / increment 05. */
  surfaces: string[] | string;
  /** The plan's use-case allow-list, or the `'all'` sentinel. Narrows the landing page's cards.
   *  Distinct from `featureAccess.useCases[x].hasAccess`, which is also false for a use case the account
   *  has not PAID for — those stay visible and locked so the upsell still works. */
  allowedUseCases: string[] | string;
  /** Plan feature: an audience with exactly one segment skips the segment-selection step. */
  autoSelectSingleSegment: boolean;
  /** This account's own client configuration. Null means the server sent none — the caller
   *  belongs to no configured client, or the backend predates the field. Either way the
   *  consumer keeps its existing unconfigured behaviour. */
  clientConfig: ClientConfig | null;
  /** The survey-chat cards this account may open, in server order. */
  surveyChatTargets: SurveyChatTarget[];
  // State
  plans: SubscriptionPlan[];
  currentSubscription: CurrentSubscription | null;
  featureAccess: FeatureAccess | null;
  usage: UsageInfo | null;
  loading: boolean;
  error: string | null;

  // Actions
  refreshSubscription: () => Promise<void>;
  checkUseCaseAccess: (useCase: string) => Promise<boolean>;
  hasFeature: (feature: string) => boolean;
  hasFeatureAccess: (useCase: string) => boolean;
  canUpgrade: () => boolean;
  getUpgradeRecommendation: () => string | null;
  isWithinLimits: (personas: number, segments: number) => boolean;

  // Checkout
  startCheckout: (planId: string) => Promise<void>;
  cancelSubscription: (atPeriodEnd?: boolean) => Promise<void>;
  reactivateSubscription: () => Promise<void>;
  openBillingPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider: React.FC<SubscriptionProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();

  // State
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [surfaces, setSurfaces] = useState<string[] | string>('all');
  const [allowedUseCases, setAllowedUseCases] = useState<string[] | string>('all');
  const [autoSelectSingleSegment, setAutoSelectSingleSegment] = useState(false);
  const [clientConfig, setClientConfig] = useState<ClientConfig | null>(null);
  const [surveyChatTargets, setSurveyChatTargets] = useState<SurveyChatTarget[]>([]);
  const [featureAccess, setFeatureAccess] = useState<FeatureAccess | null>(null);
  const [usage, setUsage] = useState<UsageInfo | null>(null);
  const [loading, setLoading] = useState(false);
  // Set exactly once, in `refreshSubscription`'s finally — a FAILED resolution is still a resolution.
  const [firstResolutionSettled, setFirstResolutionSettled] = useState(false);
  // Which account the state above belongs to, so a change of account can be detected even
  // when the render never passes through a signed-out state.
  const resolvedForRef = useRef<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Track if refresh is in progress to prevent multiple simultaneous calls
  const isRefreshingRef = useRef(false);

  /**
   * Load available plans
   */
  const loadPlans = useCallback(async () => {
    try {
      const response = await subscriptionService.getAvailablePlans();
      if (response.success && response.data) {
        setPlans(response.data);
      }
    } catch (err) {
      console.error('Failed to load plans:', err);
    }
  }, []);

  /**
   * Refresh current subscription data
   */
  const refreshSubscription = useCallback(async () => {
    // Marketplace accounts have no enterprise subscription; the routes 403 them.
    if (!isAuthenticated || user?.account_type === MARKETPLACE_ACCOUNT_TYPE) {
      return;
    }

    // Prevent multiple simultaneous calls
    if (isRefreshingRef.current) {
      return;
    }

    isRefreshingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      // Load current subscription and features
      const [subscriptionResponse, usageResponse] = await Promise.all([
        subscriptionService.getCurrentSubscription(),
        subscriptionService.getUsageDetails(),
      ]);

      if (subscriptionResponse.success && subscriptionResponse.data) {
        // The API returns nested data structure: {success: true, data: {data: {...}}}
        const actualData = subscriptionResponse.data.data || subscriptionResponse.data;
        setCurrentSubscription(actualData.currentPlan ?? null);
        setFeatureAccess(actualData.features ?? null);
        // Spec 25 / increment 05. Absent means unrestricted — an older backend, or one deployed behind this
        // build, must not empty the navigation. `'all'` is what every plan but `summit` resolves to.
        setSurfaces(actualData.surfaces ?? subscriptionResponse.data?.surfaces ?? 'all');
        // Same sentinel default as surfaces, and for the same reason: an older backend that does not send
        // this field must leave every existing client unrestricted rather than hiding all their cards.
        setAllowedUseCases(
          actualData.allowedUseCases ?? subscriptionResponse.data?.allowedUseCases ?? 'all',
        );
        // Defaults FALSE: an older backend that does not send it must not start skipping steps.
        setAutoSelectSingleSegment(
          actualData.autoSelectSingleSegment ??
            subscriptionResponse.data?.autoSelectSingleSegment ??
            false,
        );
        // Null, not an empty object: consumers distinguish "no client configured" from
        // "configured with nothing", and only the first may fall back to showing everything.
        setClientConfig(actualData.clientConfig ?? subscriptionResponse.data?.clientConfig ?? null);
        setSurveyChatTargets(
          actualData.surveyChatTargets ?? subscriptionResponse.data?.surveyChatTargets ?? [],
        );
      } else {
        console.error('❌ Subscription response failed:', subscriptionResponse);
        setError('Failed to load subscription');
      }

      if (usageResponse.success && usageResponse.data) {
        setUsage(usageResponse.data.usage);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
      setFirstResolutionSettled(true);
      isRefreshingRef.current = false;
    }
  }, [isAuthenticated, user?.account_type]);

  // Load subscription data when user is authenticated. Marketplace accounts have
  // no enterprise subscription (per-study payment) and the /api/subscription/*
  // routes 403 them, so skip the calls entirely instead of firing failing requests.
  useEffect(() => {
    // EVERY entitlement field resets when the account changes, and the loading gate re-arms with
    // it. Clearing only some of them left the PREVIOUS account's answers on screen: sign in as a
    // Kettle & Fire user, sign out, sign in as an admin, and the concept test still offered Bone
    // Broth until the page was reloaded. `firstResolutionSettled` was the other half — it latched
    // true on the first account and never reset, so the skeleton that exists precisely to avoid
    // drawing an unresolved answer never showed again.
    //
    // Keyed on the user's IDENTITY rather than on `isAuthenticated`, because switching accounts
    // need not pass through a signed-out render, and the stale values are another client's.
    const identity = isAuthenticated && user ? String(user.id ?? user.email ?? '') : null;
    if (identity !== resolvedForRef.current) {
      resolvedForRef.current = identity;
      setFirstResolutionSettled(false);
      setCurrentSubscription(null);
      setFeatureAccess(null);
      setUsage(null);
      setSurfaces('all');
      setAllowedUseCases('all');
      setAutoSelectSingleSegment(false);
      setClientConfig(null);
      setSurveyChatTargets([]);
    }

    if (isAuthenticated && user && user.account_type !== MARKETPLACE_ACCOUNT_TYPE) {
      refreshSubscription();
      loadPlans();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]); // refreshSubscription and loadPlans are stable useCallback functions

  /**
   * Check access to a specific use case
   */
  const checkUseCaseAccess = async (useCase: string): Promise<boolean> => {
    try {
      const response = await subscriptionService.checkUseCaseAccess(useCase);
      return (response.success && response.data?.access.hasAccess) || false;
    } catch {
      return false;
    }
  };

  /**
   * Check if user has a specific feature
   */
  const hasFeature = (feature: string): boolean => {
    if (!featureAccess) return false;

    // Admin users have all features
    if (featureAccess.isAdmin) return true;

    switch (feature) {
      case 'priority_support':
        return featureAccess.planFeatures.prioritySupport;
      case 'api_access':
        return featureAccess.planFeatures.apiAccess;
      case 'custom_branding':
        return featureAccess.planFeatures.customBranding;
      default:
        return false;
    }
  };

  /**
   * Check if user has access to a specific use case (synchronous version)
   */
  const hasFeatureAccess = (useCase: string): boolean => {
    if (!featureAccess) {
      // Default behavior: admin via auth context, or fallback to free user logic
      return user?.is_admin || useCase === 'survey-and-focus-groups';
    }

    // Admin users have access to all use cases (check both featureAccess.isAdmin and user.is_admin)
    if (featureAccess.isAdmin || user?.is_admin) {
      return true;
    }

    // Check if useCases object exists and use case exists and user has access to it
    if (!featureAccess.useCases || typeof featureAccess.useCases !== 'object') {
      return false;
    }

    // Try to find use case with original name first, then with normalized name (underscore to dash conversion)
    let useCaseInfo = featureAccess.useCases[useCase];
    if (!useCaseInfo) {
      const normalizedUseCase = useCase.replace(/-/g, '_');
      useCaseInfo = featureAccess.useCases[normalizedUseCase];
    }

    const hasAccess = useCaseInfo ? useCaseInfo.hasAccess : false;
    return hasAccess;
  };

  /**
   * Check if user can upgrade their plan
   */
  const canUpgrade = (): boolean => {
    if (!currentSubscription) return false;

    const planHierarchy = [
      PLAN_TIERS.FREE,
      PLAN_TIERS.STARTER,
      PLAN_TIERS.PROFESSIONAL,
      PLAN_TIERS.BUSINESS,
      PLAN_TIERS.ENTERPRISE,
    ];
    const currentIndex = planHierarchy.indexOf(currentSubscription.id as PlanTier);

    return currentIndex < planHierarchy.length - 1;
  };

  /**
   * Get upgrade recommendation
   */
  const getUpgradeRecommendation = (): string | null => {
    if (!currentSubscription || !usage) return null;

    // Check if approaching limits
    if (usage.usagePercentage > 80) {
      const planHierarchy = [
        PLAN_TIERS.FREE,
        PLAN_TIERS.STARTER,
        PLAN_TIERS.PROFESSIONAL,
        PLAN_TIERS.BUSINESS,
        PLAN_TIERS.ENTERPRISE,
      ];
      const currentIndex = planHierarchy.indexOf(currentSubscription.id as PlanTier);

      if (currentIndex < planHierarchy.length - 1) {
        return planHierarchy[currentIndex + 1];
      }
    }

    return null;
  };

  /**
   * Check if simulation parameters are within limits
   */
  const isWithinLimits = (personas: number, segments: number): boolean => {
    if (!featureAccess) return false;

    // Admin users bypass limits
    if (featureAccess.isAdmin) return true;

    const { maxPersonasPerSimulation, maxSegments } = featureAccess.planFeatures;

    // Check persona limit (-1 means unlimited)
    if (maxPersonasPerSimulation !== -1 && personas > maxPersonasPerSimulation) {
      return false;
    }

    // Check segment limit (-1 means unlimited)
    if (maxSegments !== -1 && segments > maxSegments) {
      return false;
    }

    return true;
  };

  /**
   * Start checkout process
   */
  const startCheckout = async (planId: string) => {
    try {
      const baseUrl = window.location.origin;
      const response = await subscriptionService.createCheckoutSession(
        planId,
        `${baseUrl}/subscription/success`,
        `${baseUrl}/subscription/cancelled`,
      );

      if (response.success && response.data) {
        // Redirect to Stripe checkout
        window.location.href = response.data.checkoutUrl;
      } else {
        throw new Error(response.success ? 'Failed to create checkout session' : response.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Checkout failed');
      throw err;
    }
  };

  /**
   * Cancel subscription
   */
  const cancelSubscription = async (atPeriodEnd: boolean = true) => {
    try {
      const response = await subscriptionService.cancelSubscription(atPeriodEnd);

      if (response.success) {
        await refreshSubscription();
      } else {
        throw new Error(response.error || 'Failed to cancel subscription');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Cancellation failed');
      throw err;
    }
  };

  /**
   * Reactivate cancelled subscription
   */
  const reactivateSubscription = async () => {
    try {
      const response = await subscriptionService.reactivateSubscription();

      if (response.success) {
        await refreshSubscription();
      } else {
        throw new Error(response.error || 'Failed to reactivate subscription');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reactivation failed');
      throw err;
    }
  };

  /**
   * Open Stripe billing portal
   */
  const openBillingPortal = async () => {
    try {
      const returnUrl = window.location.origin + '/dashboard';
      const response = await subscriptionService.createBillingPortal(returnUrl);

      if (response.success && response.data) {
        window.location.href = response.data.portalUrl;
      } else {
        throw new Error(response.success ? 'Failed to open billing portal' : response.error);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      throw err;
    }
  };

  const value: SubscriptionContextType = {
    // State
    entitlementsLoading: entitlementsAreLoading({
      isAuthenticated,
      accountType: user?.account_type,
      firstResolutionSettled,
    }),
    plans,
    currentSubscription,
    featureAccess,
    surfaces,
    allowedUseCases,
    autoSelectSingleSegment,
    clientConfig,
    surveyChatTargets,
    usage,
    loading,
    error,

    // Actions
    refreshSubscription,
    checkUseCaseAccess,
    hasFeature,
    hasFeatureAccess,
    canUpgrade,
    getUpgradeRecommendation,
    isWithinLimits,

    // Checkout
    startCheckout,
    cancelSubscription,
    reactivateSubscription,
    openBillingPortal,
  };

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
};

/**
 * Hook to use subscription context
 */
export const useSubscription = (): SubscriptionContextType => {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
};

export default SubscriptionContext;
